import { useGlobalProperty } from '@/hooks/useGlobalProperty';
import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { propertyAPI } from '@/services/api';
import { Package, Plus, Edit, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['Minibar', 'Amenities', 'Equipamiento', 'Limpieza', 'Otro'];

export default function InventoryPage() {
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useGlobalProperty(properties);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newProduct, setNewProduct] = useState({
    nombre: '',
    categoria: 'Minibar',
    precio_venta: '',
    cantidad_inicial: '',
    unidad: 'Unidad',
    descripcion: ''
  });

  useEffect(() => {
    fetchProperties();
  }, []);

  useEffect(() => {
    if (selectedProperty) {
      fetchProducts();
    }
  }, [selectedProperty]);

  const fetchProperties = async () => {
    try {
      const response = await propertyAPI.getAll();
      setProperties(response.data);
      if (response.data.length > 0) {
        
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/inventory/products?property_id=${selectedProperty}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/inventory/products`,
        {
          property_id: selectedProperty,
          nombre: newProduct.nombre,
          categoria: newProduct.categoria,
          precio_venta: parseFloat(newProduct.precio_venta),
          cantidad_inicial: parseInt(newProduct.cantidad_inicial),
          unidad: newProduct.unidad,
          descripcion: newProduct.descripcion || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Producto creado exitosamente');
      setDialogOpen(false);
      setNewProduct({ nombre: '', categoria: 'Minibar', precio_venta: '', cantidad_inicial: '', unidad: 'Unidad', descripcion: '' });
      fetchProducts();
    } catch (error) {
      console.error('Error creating product:', error);
      toast.error('Error al crear producto');
    }
  };

  const handleAdjustStock = async (productId, cantidad, motivo) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${BACKEND_URL}/api/inventory/products/${productId}/stock?cantidad=${cantidad}&motivo=${motivo}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Stock actualizado');
      fetchProducts();
    } catch (error) {
      console.error('Error adjusting stock:', error);
      toast.error(error.response?.data?.detail || 'Error al ajustar stock');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="inventory-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold" style={{fontFamily: 'Manrope'}}>
              Inventario
            </h1>
            <p className="text-muted-foreground mt-1">
              Gestiona productos y stock de tu propiedad
            </p>
          </div>

          <div className="flex gap-3 w-full sm:w-auto">
            {properties.length > 0 && (
              <Select value={selectedProperty} onValueChange={setSelectedProperty}>
                <SelectTrigger className="flex-1 sm:w-[240px]">
                  <SelectValue placeholder="Selecciona propiedad" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((prop) => (
                    <SelectItem key={prop.id} value={prop.id}>{prop.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-product-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Agregar Producto
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle style={{fontFamily: 'Manrope'}}>Nuevo Producto</DialogTitle>
                  <DialogDescription>Agrega un producto al inventario</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateProduct} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Nombre *</Label>
                    <Input
                      placeholder="Ej: Agua mineral 500ml"
                      value={newProduct.nombre}
                      onChange={(e) => setNewProduct({...newProduct, nombre: e.target.value})}
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Categoría</Label>
                      <Select value={newProduct.categoria} onValueChange={(v) => setNewProduct({...newProduct, categoria: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CATEGORIES.map((cat) => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Unidad</Label>
                      <Input
                        placeholder="Unidad"
                        value={newProduct.unidad}
                        onChange={(e) => setNewProduct({...newProduct, unidad: e.target.value})}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Precio de Venta *</Label>
                      <Input
                        type="number"
                        min="0"
                        placeholder="5000"
                        value={newProduct.precio_venta}
                        onChange={(e) => setNewProduct({...newProduct, precio_venta: e.target.value})}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Cantidad Inicial *</Label>
                      <Input
                        type="number"
                        min="0"
                        placeholder="10"
                        value={newProduct.cantidad_inicial}
                        onChange={(e) => setNewProduct({...newProduct, cantidad_inicial: e.target.value})}
                        required
                      />
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button type="button" variant="outline" className="flex-1" onClick={() => setDialogOpen(false)}>
                      Cancelar
                    </Button>
                    <Button type="submit" className="flex-1">
                      Crear Producto
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Products Grid */}
        {products.length === 0 ? (
          <Card className="p-12 text-center">
            <Package className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Sin productos</h3>
            <p className="text-muted-foreground mb-4">Agrega productos al inventario para comenzar</p>
            <Button onClick={() => setDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Agregar Primer Producto
            </Button>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {products.map((product) => (
              <Card key={product.id} className={product.stock_actual <= 5 ? 'border-amber-300' : ''}>
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-base">{product.nombre}</CardTitle>
                      <Badge variant="secondary" className="mt-1">{product.categoria}</Badge>
                    </div>
                    {product.stock_actual <= 5 && (
                      <AlertTriangle className="h-5 w-5 text-amber-500" />
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Stock</p>
                      <p className={`text-2xl font-bold ${product.stock_actual <= 5 ? 'text-amber-600' : ''}`}>
                        {product.stock_actual}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Precio</p>
                      <p className="text-2xl font-bold">${product.precio_venta.toLocaleString()}</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 mt-4">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => handleAdjustStock(product.id, 1, 'compra')}
                    >
                      +1
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => handleAdjustStock(product.id, -1, 'ajuste_manual')}
                      disabled={product.stock_actual <= 0}
                    >
                      -1
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
